<template>
  <div
    class="flex flex-col gap-5 py-6 rounded-lg bg-utility-blue-50 border-sm border-utility-blue-200 border-opacity-100 shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05)]"
  >
    <div class="flex gap-2 justify-between items-start px-6">
      <div>
        <p class="text-utility-blue-700 text-lg font-semibold">
          {{ t('Early Deterioration Service') }}
        </p>
        <p class="text-subtitle text-sm">{{ t('Information about the backend service') }}</p>
      </div>
      <button data-testid="btn-close" @click="$emit('close-sidebar')">
        <VIcon color="tree-node" icon="close" size="20" />
      </button>
    </div>
    <div
      class="flex flex-col gap-5 flex-1 overflow-y-auto min-h-0 px-6 text-subtitle text-sm pb-[1px]"
    >
      <p v-if="!hasAcceptedEdsLimitation">
        {{
          t(
            'This backend service is intended to be used by healthcare professionals that have undergone medical school or nursing programs, and that have completed an introductory tutorial for this backend service. Ensure that you are also familiar with the Instructions for Use of this backend service.'
          )
        }}
      </p>
      <template v-else>
        <p v-if="!udiData && !isLoadingUdiData" class="text-[#D92D20]">
          {{
            t(
              'Loading of elements has failed. See below for details. Contact info@tcc-clinicalsolutions.de or check the instruction for use for further MDR information.'
            )
          }}
        </p>
        <div>
          <p class="font-semibold">{{ t('Reference Score:') }}</p>
          <p>0-4 {{ t('Low risk') }}</p>
          <p>5-6 {{ t('Medium risk') }}</p>
          <p>7-18 {{ t('High risk') }}</p>
        </div>
        <div>
          <p class="font-semibold">{{ t('Explanation') }}</p>
          <p>
            {{
              t(
                'The backend service uses the values of vital signs and the volume of oxygen supplementation to calculate the score. The vital signs that the backend service uses are heart rate, respiratory rate, oxygen saturation, blood pressure and body temperature. The values of the vital signs may not be older than 24 hours. There is no time limit for the volume of oxygen supplementation. All values are mandatory.'
              )
            }}
          </p>
        </div>
        <div>
          <p class="font-semibold">{{ t('Important') }}</p>
          <p>
            {{
              t(
                'The displayed data do not replace a medical examination or laboratory analysis. Have all values verified by a laboratory or confirmed through diagnostic testing. TCC recommends that you do not use the algorithm for patients in the ICU.'
              )
            }}
          </p>
        </div>
      </template>
      <div>
        <p class="font-semibold">{{ t('Contraindications') }}</p>
        <p>{{ t('Do not use the backend service for the following patient population:') }}</p>
        <ul class="list-disc list-inside mt-2 ml-1.5">
          <li>{{ t('Children under the age of 18') }}</li>
          <li>{{ t('Pregnant women') }}</li>
        </ul>
        <p class="mt-2">
          {{
            t(
              'Pay close attention when interpreting the score of the backend service in patients with chronic obstructive pulmonary disease (COPD).'
            )
          }}
        </p>
      </div>
      <div v-if="hasAcceptedEdsLimitation && !isLoadingUdiData" class="flex flex-col gap-5">
        <UdiItem
          v-for="item in udiItems"
          :key="item.key"
          :udi-key="item.key"
          :text="item.text"
          :is-error="item.isError"
        />
      </div>
      <VForm
        ref="formRef"
        data-testid="consent-form"
        v-else
        class="flex flex-col gap-10"
        @submit.prevent="handleConfirm"
      >
        <VCheckbox
          data-testid="consent-checkbox"
          v-model="consent"
          :label="t('I have read and understood all limitations and contraindications.')"
          :rules="[consentRule]"
          class="eds-consent-checkbox"
          density="compact"
          hide-details="auto"
          base-color="border-primary"
        />
        <VBtn
          data-testid="confirm-button"
          color="primary"
          class="button-large self-end w-fit !h-10"
          :loading="isConsentAcceptanceLoading"
          type="submit"
        >
          {{ t('Accept and show score') }}
        </VBtn>
      </VForm>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useEdsPlugin } from '@/composables/useEdsPlugin'
import { EdsService } from '@/services/telehealthApi/EdsService'
import { UserSettingsService } from '@/services/telehealthApi/UserSettingsService'
import { NoticeType } from '@/stores/models/Notice'
import { useNoticeStore } from '@/stores/notice'
import { computed, ref } from 'vue'
import type { PluginUdiDto } from '@/generated'
import { useI18n } from 'vue-i18n'
import { VBtn, VCheckbox, VForm } from 'vuetify/components'
import { useEdsStore } from '@/stores/eds'
import { storeToRefs } from 'pinia'
import { onMounted } from 'vue'
import { format, isValid, parse } from 'date-fns'
import { DateFormatter } from '@/enums/DateFormatter'
import UdiItem from '../atoms/UdiItem.vue'
import { usePatientsWithVitalSignsStore } from '@/stores/patientsWithVitalSigns'

defineEmits<{
  (e: 'close-sidebar'): void
}>()

const { t } = useI18n()
const noticeStore = useNoticeStore()
const patientsStore = usePatientsWithVitalSignsStore()
const { setUdiData } = useEdsStore()
const {
  hasAcceptedEdsLimitation,
  REQUIRED_EDS_LIMITATIONS_SCOPE,
  REQUIRED_EDS_LIMITATIONS_VERSION
} = useEdsPlugin()

const { udiData } = storeToRefs(useEdsStore())
const formRef = ref<InstanceType<typeof VForm>>()
const consent = ref<boolean>(false)
const isConsentAcceptanceLoading = ref<boolean>(false)
const isLoadingUdiData = ref(false)

onMounted(async () => {
  if (hasAcceptedEdsLimitation.value && !udiData.value) {
    await fetchUdiData()
  }
})

const fetchUdiData = async () => {
  if (isLoadingUdiData.value) return

  isLoadingUdiData.value = true
  try {
    const udiResponse = await EdsService.getUdi()
    setUdiData(udiResponse)
  } catch (e) {
    console.error('Failed to fetch UDI data: ', e)
    noticeStore.add({
      type: NoticeType.ERROR,
      message: t('Failed to fetch UDI data')
    })
    setUdiData(null)
  } finally {
    isLoadingUdiData.value = false
  }
}

const udiKeys: (keyof PluginUdiDto)[] = [
  'ceMark',
  'instructionsForUse',
  'caution',
  'type',
  'productName',
  'dateOfManufacture',
  'manufacturer',
  'udi',
  'labelIdentification'
]

const udiItems = computed(() => {
  const isError = !udiData.value

  return udiKeys.map((key) => {
    const formattedText = (() => {
      if (key === 'manufacturer') {
        return [udiData.value?.manufacturer, udiData.value?.address].filter(Boolean).join('\n')
      }
      if (key === 'udi') {
        return udiData.value?.udi?.match(/(\([^)]+\)[^(]+)/g) ?? udiData?.value?.udi
      }
      if (key === 'dateOfManufacture') {
        const parsedDate = parse(udiData.value?.dateOfManufacture || '', 'yyMMdd', new Date())
        return isValid(parsedDate)
          ? format(parsedDate, DateFormatter.DATE_FULL_YEAR)
          : udiData.value?.dateOfManufacture
      }
      return udiData.value?.[key]
    })()

    return {
      key,
      text: formattedText,
      isError
    }
  })
})

const consentRule = (v: boolean) =>
  v || t('Accepting these terms is mandatory before you can continue.')

const handleConfirm = async () => {
  if (isConsentAcceptanceLoading.value) return

  const { valid } = await formRef.value!.validate()
  if (!valid) return

  isConsentAcceptanceLoading.value = true

  try {
    await EdsService.acceptEdsLimitation({
      version: REQUIRED_EDS_LIMITATIONS_VERSION,
      scope: REQUIRED_EDS_LIMITATIONS_SCOPE
    })
  } catch (error) {
    noticeStore.add({
      type: NoticeType.ERROR,
      message: t('Failed to accept EDS terms. Please try again later.')
    })
    isConsentAcceptanceLoading.value = false
    return
  }

  try {
    const isSynced = await UserSettingsService.syncUserWithBackend()

    if (!isSynced) {
      throw new Error('Sync failed')
    }

    noticeStore.add({
      type: NoticeType.SUCCESS,
      message: t('EDS terms accepted')
    })
  } catch (error) {
    noticeStore.add({
      type: NoticeType.WARNING,
      message: t('Terms accepted, but failed to sync user data. Please refresh the page.')
    })
  } finally {
    isConsentAcceptanceLoading.value = false
  }

  await Promise.all([fetchUdiData(), patientsStore.getCasesWithVitalSigns()])
}
</script>
<style scoped>
.eds-consent-checkbox :deep(.v-selection-control) {
  align-items: flex-start;
  gap: 4px;
}

.eds-consent-checkbox :deep(.v-label) {
  font-size: 14px;
  color: #414651;
  font-weight: 600;
}

.eds-consent-checkbox :deep(.v-input__details) {
  padding-inline-start: 32px;
}

.eds-consent-checkbox.v-input--error :deep(.v-messages) {
  color: #d92d20;
}

.eds-consent-checkbox.v-input--error :deep(.v-selection-control__input > .v-icon) {
  color: #f04438;
}
</style>
